import HttpFactory from '../factory'
import type { EpisodeOfCarePolicies, PatientPolicies, PractitionerPolicies } from '../types/auth/policies'
import type { CibaCredentials, CibaInitResult, OpenIdConnectCredentials, TokenSet } from '../types/auth/psc'
import type { CustomToken, PractitionerRoles, UpdatePractitionerRoles } from '../types/auth/role'

class AuthModule extends HttpFactory<any[]> {
  private RESOURCE = '/auth'
  private RESOURCE_POLICIES = '/auth/policies'
  private RESOURCE_PRACTITIONERS = '/auth/practitioners'
  private RESOURCE_PSC_CIBA = '/auth/external/pro-sante-connect/ciba'
  private RESOURCE_EXTERNAL_LOGIN = `/external-users/log-in-with-challenge`
  private PATH_SESSION = '/user/session'
  private RESOURCE_RESET_PASSWORD_REQUEST = '/user/reset-password-request'
  private RESOURCE_RESET_PASSWORD = '/user/reset-password'

  private cibaTokenController: AbortController = new AbortController()

  async surveyLogin(token: string): Promise<CustomToken> {
    return await this.call<any>('POST', `${this.RESOURCE}/surveys/log-in-with-challenge`, { token }).catch((error) => {
      navigateTo('/survey/expired')
      return Promise.reject(error)
    })
  }
  async deleteSession(): Promise<any> {
    return await this.call<any>('DELETE', `${this.PATH_SESSION}`)
  }
  async externalLogin(token: string): Promise<CustomToken> {
    return await this.call<any>('POST', `${this.RESOURCE}${this.RESOURCE_EXTERNAL_LOGIN}`, { token }).catch((error) => {
      return Promise.reject(error)
    })
  }

  // TODO: update the payload, to have only the new role in param ({id? roles})
  async saveRoles(obj: any, roles: any): Promise<PractitionerRoles> {
    const data: UpdatePractitionerRoles = {
      roles,
    }
    if (obj.revision) {
      return await this.call<any>('PATCH', `${this.RESOURCE_PRACTITIONERS}/${obj.id}?revision=${obj.revision}`, data)
    } else {
      return await this.call<any>('PATCH', `${this.RESOURCE_PRACTITIONERS}/${obj.id}`, data)
    }
  }

  async resetPasswordRequest(data: any): Promise<any> {
    return await this.call<any>('POST', `${this.RESOURCE_RESET_PASSWORD_REQUEST}`, data)
  }

  async resetPassword(data: any): Promise<any> {
    return await this.call<any>('POST', `${this.RESOURCE_RESET_PASSWORD}`, data)
  }

  // roles
  async getRoles(id: string): Promise<PractitionerRoles> {
    let roles = []
    const response = await this.call<any>('GET', `${this.RESOURCE_PRACTITIONERS}/${id}`)
    roles = response
    return roles
  }

  async getPolicies(
    type: 'patients' | 'practitioner' | 'episodes-of-care',
    id: string = '',
  ): Promise<PatientPolicies | EpisodeOfCarePolicies | PractitionerPolicies> {
    return await this.call<any>('GET', `${this.RESOURCE_POLICIES}/${type}/${id}`)
  }

  async loginWithOTP(otp: string): Promise<any> {
    try {
      const result: CustomToken = await this.call<any>('POST', `${this.RESOURCE}/users/log-in-with-totp `, {
        totp: otp,
      })
      return result
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   * Generate OTP for a specific user.
   *
   * @param userId The ID of the user to generate OTP for
   * @returns the OTP code and expiration date
   */
  async generateTOTP(userId: string): Promise<{ totp: string; expiresAt: string }> {
    try {
      const result = await this.call<any>('PUT', `/users/${userId}/totp`)
      return result
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   * Processes ProSanteConnect code flow callback.
   * It will get the credential from backend then it could be used to perform a Firebase `signInWithCredential`.
   *
   * @param params The all the query parameters added by the OIDC provider in the redirection URL.
   * @returns the ceredential for the user
   */
  async getCredentialFromPSC(params: any): Promise<OpenIdConnectCredentials> {
    try {
      const { credential }: TokenSet = await this.call<any>(
        'GET',
        `${this.RESOURCE}/external/pro-sante-connect`,
        undefined,
        {
          params,
          credentials: 'include',
        },
      )

      if (!credential) throw new Error('Could not fetch the credentials')
      return credential
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   * Processes FastConnect code flow callback.
   * It will get the credential from backend then it could be used to perform a Firebase `signInWithCredential`.
   *
   * @param params The all the query parameters added by the OIDC provider in the redirection URL.
   * @returns the ceredential for the user
   */
  async getCredentialFromFC(params: any): Promise<OpenIdConnectCredentials> {
    try {
      const { credential }: TokenSet = await this.call<any>('GET', `${this.RESOURCE}/external/france-connect`, undefined, {
        params,
        credentials: 'include',
      })

      if (!credential) throw new Error('Could not fetch the credentials')
      return credential
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   * Initiate CIBA code flow.
   *
   * @param params The rpps of the practionner.
   * @returns the code for ciba
   */
  async initiateCIBA(rpps: string): Promise<CibaInitResult> {
    try {
      const result: CibaInitResult = await this.call<any>(
        'POST',
        `${this.RESOURCE_PSC_CIBA}`,
        { rpps },
        { credentials: 'include' },
      )

      if (!result.code) throw new Error('Could not fetch the ciba code')

      return result
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   * Get CIBA token.
   *
   * @returns the code for ciba
   */
  async getCIBACredentials(): Promise<CibaCredentials> {
    try {
      const result: any = await this.call<any>('GET', `${this.RESOURCE_PSC_CIBA}`, undefined, {
        credentials: 'include',
      })
      return result.credential
    } catch (error) {
      return Promise.reject(error)
    }
  }

  async trackDeviceActivity(practitionerId: string, deviceId: string, data: any): Promise<any> {
    try {
      const result: any = await this.call<any>(
        'PUT',
        `${this.RESOURCE_PRACTITIONERS}/${practitionerId}/sessions/${deviceId}`,
        data,
      )
      return result
    } catch (error) {}
  }

  async logoutPractitioner(practitionerId: string, deviceId: string): Promise<any> {
    try {
      const result: any = await this.call<any>(
        'DELETE',
        `${this.RESOURCE_PRACTITIONERS}/${practitionerId}/sessions/${deviceId}`,
      )
      return result
    } catch (error) {}
  }

  abortCibaTokenPulling() {
    if (this.cibaTokenController) this.cibaTokenController.abort()
  }
}

export default AuthModule
